import styled, { css, keyframes } from "styled-components";
import tokens from "../../theme/tokens";

/**
 * AIStatusBadge — visual chrome.
 *
 * Each badge is a 14×14 inline circle (or icon) that piggybacks on the
 * tokens used by StatusChip / ConfidencePill so it reads as a sibling of
 * the existing status surface, not a standalone affordance. The three
 * states map 1:1 to:
 *
 *   running   → pulsing filled dot, processing tone
 *   completed → check-in-circle, ready tone
 *   failed    → triangle, failed tone
 *
 * Sizing is deliberately tight (smaller than the 20px ConfidencePill)
 * because the badge stacks beside two other pills in the CATEGORY cell;
 * a third 20px pill would push the cell beyond its 300px max-width on
 * narrow viewports. The 14×14 dot reads as a "marker" rather than a
 * "chip" and keeps the row scannable.
 *
 * History: a fourth `briefing` variant (brand-blue solid dot) existed
 * pre-2026-04-29 and was removed when matter mode dropped the standalone
 * Briefing tab; see `AIStatusBadge.js` header for the rationale.
 */

const pulse = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(12, 83, 187, 0.55); }
  70%  { box-shadow: 0 0 0 5px rgba(12, 83, 187, 0); }
  100% { box-shadow: 0 0 0 0 rgba(12, 83, 187, 0); }
`;

const baseDot = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  border-radius: 9999px;
  border: 1px solid transparent;
  flex-shrink: 0;
  font-size: 9px;
  line-height: 1;
  font-family: ${tokens.typography.fontFamily};
`;

const variantStyles = {
  running: css`
    background: ${tokens.status.processing.fg};
    border-color: ${tokens.status.processing.fg};
    color: ${tokens.text.onAccent};
    animation: ${pulse} 1.6s ease-in-out infinite;
  `,
  completed: css`
    background: ${tokens.status.ready.bg};
    border-color: ${tokens.status.ready.border};
    color: ${tokens.status.ready.fg};
  `,
  failed: css`
    background: ${tokens.status.failed.bg};
    border-color: ${tokens.status.failed.border};
    color: ${tokens.status.failed.fg};
  `,
};

/**
 * The badge itself. Rendered as a `button` when an onActivate callback is
 * supplied (Phase 6 stretch: row-row click-to-open) and as a `span`
 * otherwise — keyboard focus + the cursor-pointer affordance only show up
 * when there's actually something to click.
 */
export const BadgeRoot = styled.span.attrs((p) => ({
  as: p.$interactive ? "button" : "span",
  type: p.$interactive ? "button" : undefined,
}))`
  ${baseDot}
  ${(p) => variantStyles[p.$variant] || variantStyles.running}

  ${(p) =>
    p.$interactive
      ? css`
          cursor: pointer;
          padding: 0;

          &:focus-visible {
            outline: 2px solid ${tokens.border.focus};
            outline-offset: 2px;
          }

          &:hover {
            opacity: 0.85;
          }
        `
      : css`
          cursor: default;
          padding: 0;
        `}
`;

/**
 * Inline glyph wrapper — keeps the SVG aligned at 10×10 inside the 14×14
 * dot regardless of variant. The check + triangle glyphs ride this
 * wrapper; the running variant renders an empty body and relies on the
 * pulsing dot fill itself.
 */
export const BadgeGlyph = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 10px;
  height: 10px;
  line-height: 1;
`;
